// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The comfort settings (M3-40, NFR-024, NFR-026).
//
// Two preferences the whole client reads, kept together because they are the same kind of thing
// and because a player looking for one will look where the other is.
//
// **Reduce flashing is not the same preference as reduce motion, and conflating them is the
// mistake this file exists to avoid.** They overlap — somebody who asks the operating system for
// less motion is very likely to want fewer strobes, so the system preference *turns this on* —
// but they come apart in both directions. A photosensitive player may be perfectly happy with a
// panning camera. A player who gets motion sick may want the machine to flash exactly as a real
// one does. So the system preference is a **default**, not a lock: `prefers-reduced-motion` turns
// flash reduction on, and an explicit choice overrides it in either direction.
//
// That is the opposite of how `MotionPreference` treats the same signal, deliberately.
// `prefers-reduced-motion` *wins* there, by multiplication, because camera shake is precisely
// what the operating system's preference is about and an effect that has to remember to ask is
// an effect that will one day forget. Flashing is a different hazard with a different audience,
// and 01 §8.4 asks for it to have a control of its own.

import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'
import { stampPreference } from '../storage/prefStamps'
import { prefersReducedMotion } from '../world/motionPreference'

/** `'on'` and `'off'` are the player's own answer; absent means "follow the system". */
export const FLASHING_KEY = 'hcc.pinball.reduce-flashing'

/** How much of the machine's own movement the player wants, as a stored intensity. */
export const MOTION_KEY = 'hcc.pinball.motion-intensity'

/** The three states of the flashing control. `system` is the default and is not "off". */
export type FlashingChoice = 'system' | 'on' | 'off'

export const FLASHING_CHOICES: readonly FlashingChoice[] = ['system', 'on', 'off']

/** What the player last chose, or `system`. */
export function readFlashingChoice(storage: StorageLike | undefined = safeStorage()): FlashingChoice {
  const stored = readLocal(storage, FLASHING_KEY)
  return stored === 'on' || stored === 'off' ? stored : 'system'
}

export function writeFlashingChoice(
  choice: FlashingChoice,
  storage: StorageLike | undefined = safeStorage(),
): void {
  // Stamped before either branch, because **`system` is a choice and not an absence of one**
   // (07 §7). The row is removed either way, so without this the account would keep whatever was
   // last set and hand it straight back on the next sign-in.
  stampPreference('flashing', storage)
  if (choice === 'system') {
    // Removed rather than stored as the word "system", so "follow the system" keeps following it
    // when the system changes — and so a player who has never touched this has no row at all.
    removeLocal(storage, FLASHING_KEY)
    return
  }
  writeLocal(storage, FLASHING_KEY, choice)
}

/**
 * Should the machine flash less, right now?
 *
 * The one question the renderer asks. Resolved from the choice and the system preference in that
 * order, so there is exactly one place the two are combined.
 */
export function reduceFlashing(
  choice: FlashingChoice = readFlashingChoice(),
  systemReduced: boolean = prefersReducedMotion(),
): boolean {
  if (choice === 'on') return true
  if (choice === 'off') return false
  return systemReduced
}

/**
 * The stored motion intensity, 0…1.
 *
 * `MotionPreference` owns the live value and the system override; this is only where it is kept
 * between visits. One is a runtime object and the other is a string in a browser, and keeping
 * them apart is what lets the runtime be tested without a browser at all.
 */
export function readMotionIntensity(storage: StorageLike | undefined = safeStorage()): number {
  const raw = readLocal(storage, MOTION_KEY)
  // The empty string has to be checked before the number, because `Number('')` is **zero** — so
  // a blank value would read as "no movement at all" and quietly hand somebody a flat machine
  // they never asked for and could not explain.
  if (raw === undefined || raw.trim() === '') return 1
  const stored = Number(raw)
  if (!Number.isFinite(stored) || stored < 0 || stored > 1) return 1
  return stored
}

export function writeMotionIntensity(
  intensity: number,
  storage: StorageLike | undefined = safeStorage(),
): void {
  const clamped = Number.isFinite(intensity) ? Math.min(Math.max(intensity, 0), 1) : 1
  writeLocal(storage, MOTION_KEY, String(clamped))
  stampPreference('motion', storage)
}

// ------------------------------------------------------------------------------------------
// Haptics (M3-42)
// ------------------------------------------------------------------------------------------

/** Whether the phone buzzes. `'on'` or `'off'`; absent means the default. */
export const HAPTICS_KEY = 'hcc.pinball.haptics'

/**
 * On unless the player turned it off.
 *
 * **The opposite default from the device-motion offer next door, and for a reason.** That one asks
 * the platform for a *permission* and can only be granted by a prompt, so it starts off and is
 * offered. This one needs no permission, costs nothing when the device has no motor, and is the
 * feedback that makes a flipper feel like a flipper — a player who has to go and find a switch
 * before the machine responds to them has already formed their opinion of it.
 *
 * It stays a setting because a buzz is genuinely unwanted by some people: in a quiet room, on a
 * desk, or for anybody who finds vibration unpleasant. `hapticsSupported()` decides whether the
 * control is offered at all, so an iPhone is never shown a switch that does nothing.
 */
export function readHaptics(storage: StorageLike | undefined = safeStorage()): boolean {
  return readLocal(storage, HAPTICS_KEY) !== 'off'
}

export function writeHaptics(on: boolean, storage: StorageLike | undefined = safeStorage()): void {
  writeLocal(storage, HAPTICS_KEY, on ? 'on' : 'off')
  stampPreference('haptics', storage)
}
